// MIGRATED FROM: Solar API only
// MIGRATION DATE: 2024-08-24
// CHANGE TYPE: ENHANCED - Integrated api-client contracts with cheetah community service
// BACKWARD COMPATIBLE: Yes - All existing Solar API methods preserved

import axios, { AxiosInstance } from 'axios'
import { UserApiDocument, CommunityMedicalTip, UserApiCommunityOfDoctorReader } from '@unni/api-client'
import {
  CommunityMedicalTipPagination,
  CommunityMedicalTipView,
} from '@unni/api-client/src/community/UserApiCommunityMedicalTip'
import { PageSortField, PageSortOrder, SpringPaginationResult } from '@unni/api-client/src/community/pagination'
import { getEnv } from '@unni/platform'

import {
  PostCommunityDocumentParam,
  PostCommunityDocumentResponse,
  ThumbUpRequest,
  ThumbUpResponse,
  SubscribeRequest,
  SubscribeResponse,
  CommentListRequest,
  CommentCreateRequest,
  CommentLikeRequest,
  CommentLikeResponse,
  HotDocumentsRequest,
  WaitingDocumentsRequest,
  SubscribedDocumentsRequest,
  DoctorCommunityRequest,
  DoctorCommentRequest,
  PersonalizedIdsRequest,
  PersonalizedIdsResponse,
  TranslationRequest,
  TranslationResponse,
} from './community-document.types'
import {
  CommunityDocumentComment,
  CommunityRecommendation,
  DocumentByDoctor,
  DocumentCategory,
  GetCommentsByDoctorContent,
  GetDocumentDetailResponse,
  GetDocumentListResponse,
  GetDocumentsParams,
  SpringPaginationStandardFetcherResponse,
} from './community-document.types'

import { buildApiEndpoint, createSolarApiClient } from '@/shared/libs/api-client'
import { ServerResponse } from '@/shared/api/api.shared.types'

export interface GetDocumentsResponse {
  reason: 'SUCCESS' | 'FAIL'
  msgCode: string | null
  data: GetDocumentListResponse[]
  draw: number
  start: number
  length: number
  recordsTotal: number
  recordsFiltered: number
  succeeded: boolean
}

export class CommunityDocumentService {
  // API-client integration via composition
  private readonly documentApiReader: UserApiDocument
  private readonly medicalTipApiReader: CommunityMedicalTip
  private readonly doctorApiReader: UserApiCommunityOfDoctorReader

  constructor(private readonly apiClient: AxiosInstance) {
    // Initialize API-client readers
    this.documentApiReader = new UserApiDocument({ axios: apiClient })
    this.medicalTipApiReader = new CommunityMedicalTip(apiClient)
    this.doctorApiReader = new UserApiCommunityOfDoctorReader({ axios: apiClient })
  }

  async getDocumentCategories() {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/community/queries/community-categories/process',
        data: {
          memberType: 'MEMBER',
          permission: 'READ',
        },
      },
      'solar',
    )
    const response = await this.apiClient.request<ServerResponse<DocumentCategory[]>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 카테고리 조회 실패')
    }
    return response.data.data
  }

  async postDocument(params: PostCommunityDocumentParam): Promise<PostCommunityDocumentResponse> {
    // Ensure categoryId is provided - default to 1 (FREE_TALK) if not specified
    const requestParams = {
      ...params,
      categoryId: params.categoryId ?? 1, // Default to FREE_TALK category
    }

    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/document',
        params: requestParams, // Use params instead of data to match server expectation
      },
      'solar',
    )
    const response = await this.apiClient.request<ServerResponse<PostCommunityDocumentResponse>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 문서 작성 실패')
    }
    return response.data.data
  }

  async getDocuments(params: GetDocumentsParams): Promise<GetDocumentsResponse> {
    const config = buildApiEndpoint(
      {
        method: 'GET',
        url: '/search/document',
        headers: {
          deviceType: 'BROWSER',
        },
        params,
      },
      'solar',
    )
    const response = await this.apiClient.request(config)
    if (response.status !== 200) {
      throw new Error('[Community] 문서 목록 조회 실패')
    }

    return response.data as GetDocumentsResponse
  }

  async getDocumentDetail(id: string): Promise<GetDocumentDetailResponse> {
    const config = buildApiEndpoint(
      {
        method: 'GET',
        url: `/document/${id}`,
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )
    const response = await this.apiClient.request<ServerResponse<GetDocumentDetailResponse>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 문서 상세 조회 실패')
    }
    return response.data.data
  }

  async deleteDocument(documentId: number): Promise<void> {
    const config = buildApiEndpoint(
      {
        method: 'DELETE',
        url: `/document/${documentId}`,
      },
      'solar',
    )
    const response = await this.apiClient.request<ServerResponse<unknown>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 문서 삭제 실패')
    }
  }

  async thumbUpDocument(documentId: number, memberId: number): Promise<GetDocumentDetailResponse>
  async thumbUpDocument(request: ThumbUpRequest): Promise<ThumbUpResponse>
  async thumbUpDocument(
    documentIdOrRequest: number | ThumbUpRequest,
    memberId?: number,
  ): Promise<GetDocumentDetailResponse | ThumbUpResponse> {
    if (typeof documentIdOrRequest === 'object') {
      // New API: return ThumbUpResponse
      const documentId = documentIdOrRequest.documentId
      const memberIdValue = documentIdOrRequest.memberId
      const config = buildApiEndpoint(
        {
          method: 'POST',
          url: `/document/${documentId}/thumbUp?memberId=${memberIdValue}`,
          headers: {
            deviceType: 'BROWSER',
          },
        },
        'solar',
      )
      const response = await this.apiClient.request<ServerResponse<GetDocumentDetailResponse>>(config)
      if (response.data.succeeded === false) {
        throw new Error(response.data.reason ?? '[Community] 좋아요 실패')
      }
      return {
        documentId,
        thumbUpCount: response.data.data.thumbUpCount || 0,
        hasThumbUp: true,
      }
    }
    // Original implementation
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: `/document/${documentIdOrRequest}/thumbUp?memberId=${memberId}`,
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )
    const response = await this.apiClient.request<ServerResponse<GetDocumentDetailResponse>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 좋아요 실패')
    }
    return response.data.data
  }

  async cancelThumbUpDocument(documentId: number, memberId: number): Promise<GetDocumentDetailResponse>
  async cancelThumbUpDocument(request: ThumbUpRequest): Promise<ThumbUpResponse>
  async cancelThumbUpDocument(
    documentIdOrRequest: number | ThumbUpRequest,
    memberId?: number,
  ): Promise<GetDocumentDetailResponse | ThumbUpResponse> {
    if (typeof documentIdOrRequest === 'object') {
      // New API: return ThumbUpResponse
      const documentId = documentIdOrRequest.documentId
      const memberIdValue = documentIdOrRequest.memberId
      const config = buildApiEndpoint(
        {
          method: 'DELETE',
          url: `/document/${documentId}/thumbUp?memberId=${memberIdValue}`,
          headers: {
            deviceType: 'BROWSER',
          },
        },
        'solar',
      )
      const response = await this.apiClient.request<ServerResponse<GetDocumentDetailResponse>>(config)
      if (response.data.succeeded === false) {
        throw new Error(response.data.reason ?? '[Community] 좋아요 취소 실패')
      }
      return {
        documentId,
        thumbUpCount: response.data.data.thumbUpCount || 0,
        hasThumbUp: false,
      }
    }
    // Original implementation
    const config = buildApiEndpoint(
      {
        method: 'DELETE',
        url: `/document/${documentIdOrRequest}/thumbUp?memberId=${memberId}`,
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )
    const response = await this.apiClient.request<ServerResponse<GetDocumentDetailResponse>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 좋아요 취소 실패')
    }
    return response.data.data
  }

  async subscribeDocument(documentId: number, memberId: number): Promise<{ documentId: number; subscribeCount: number }>
  async subscribeDocument(request: SubscribeRequest): Promise<SubscribeResponse>
  async subscribeDocument(
    documentIdOrRequest: number | SubscribeRequest,
    memberId?: number,
  ): Promise<{ documentId: number; subscribeCount: number } | SubscribeResponse> {
    if (typeof documentIdOrRequest === 'object') {
      // New API: return SubscribeResponse
      const documentId = documentIdOrRequest.documentId
      const memberIdValue = documentIdOrRequest.memberId
      const config = buildApiEndpoint(
        {
          method: 'POST',
          url: `/document/${documentId}/subscribe?memberId=${memberIdValue}`,
          headers: {
            deviceType: 'BROWSER',
          },
        },
        'solar',
      )
      const response =
        await this.apiClient.request<ServerResponse<{ documentId: number; subscribeCount: number }>>(config)
      if (response.data.succeeded === false) {
        throw new Error(response.data.reason ?? '[Community] 구독 실패')
      }
      return {
        documentId,
        subscriberCount: response.data.data.subscribeCount,
        hasSubscribed: true,
      }
    }
    // Original implementation
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: `/document/${documentIdOrRequest}/subscribe?memberId=${memberId}`,
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )
    const response =
      await this.apiClient.request<ServerResponse<{ documentId: number; subscribeCount: number }>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 구독 실패')
    }
    return response.data.data
  }

  async unsubscribeDocument(
    documentId: number,
    memberId: number,
  ): Promise<{ documentId: number; subscribeCount: number }>
  async unsubscribeDocument(request: SubscribeRequest): Promise<SubscribeResponse>
  async unsubscribeDocument(
    documentIdOrRequest: number | SubscribeRequest,
    memberId?: number,
  ): Promise<{ documentId: number; subscribeCount: number } | SubscribeResponse> {
    if (typeof documentIdOrRequest === 'object') {
      // New API: return SubscribeResponse
      const documentId = documentIdOrRequest.documentId
      const memberIdValue = documentIdOrRequest.memberId
      const config = buildApiEndpoint(
        {
          method: 'DELETE',
          url: `/document/${documentId}/subscribe?memberId=${memberIdValue}`,
          headers: {
            deviceType: 'BROWSER',
          },
        },
        'solar',
      )
      const response =
        await this.apiClient.request<ServerResponse<{ documentId: number; subscribeCount: number }>>(config)
      if (response.data.succeeded === false) {
        throw new Error(response.data.reason ?? '[Community] 구독 취소 실패')
      }
      return {
        documentId,
        subscriberCount: response.data.data.subscribeCount,
        hasSubscribed: false,
      }
    }
    // Original implementation
    const config = buildApiEndpoint(
      {
        method: 'DELETE',
        url: `/document/${documentIdOrRequest}/subscribe?memberId=${memberId}`,
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )
    const response =
      await this.apiClient.request<ServerResponse<{ documentId: number; subscribeCount: number }>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 구독 취소 실패')
    }
    return response.data.data
  }

  async getComments(documentId: string): Promise<CommunityDocumentComment[]>
  async getComments(request: CommentListRequest): Promise<CommunityDocumentComment[]>
  async getComments(documentIdOrRequest: string | CommentListRequest): Promise<CommunityDocumentComment[]> {
    let url: string
    let params: Record<string, any>

    if (typeof documentIdOrRequest === 'string') {
      // Backward compatibility: getComments(documentId: string)
      url = `/document/${documentIdOrRequest}/comment`
      params = { start: 0, length: -1 }
    } else {
      // New API: getComments(request: CommentListRequest)
      const request = documentIdOrRequest
      url = `/document/${request.documentId}/comment`
      params = {
        start: request.start || 0,
        length: request.length || 20,
        sort: request.sort || 'createTime',
      }
    }

    const config = buildApiEndpoint(
      {
        method: 'GET',
        url,
        params,
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )
    const response = await this.apiClient.request<ServerResponse<CommunityDocumentComment[]>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 댓글 조회 실패')
    }
    return response.data.data
  }

  async writeComment(params: {
    documentId: number
    contents: string
    calleeId?: number
    replyCommentId?: number
  }): Promise<CommunityDocumentComment>
  async writeComment(request: CommentCreateRequest): Promise<CommunityDocumentComment>
  async writeComment(
    paramsOrRequest:
      | {
          documentId: number
          contents: string
          calleeId?: number
          replyCommentId?: number
        }
      | CommentCreateRequest,
  ): Promise<CommunityDocumentComment> {
    const { documentId, contents, calleeId, replyCommentId } = paramsOrRequest
    const url = `/document/${documentId}/comment`
    const queryParams = new URLSearchParams()
    if (calleeId) {
      queryParams.set('calleeId', calleeId.toString())
    }
    if (replyCommentId) {
      queryParams.set('replyCommentId', replyCommentId.toString())
    }
    if (contents) {
      queryParams.set('contents', contents.trim())
    }

    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: url + (queryParams.toString() ? '?' + queryParams.toString() : ''),
        headers: {
          deviceType: 'BROWSER',
        },
        data: {
          contents: contents.trim(),
          calleeId,
          replyCommentId,
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<ServerResponse<CommunityDocumentComment>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 댓글 작성 실패')
    }
    return response.data.data
  }

  // Alias for new API compatibility
  createComment = this.writeComment

  async likeComment(commentId: number): Promise<CommunityDocumentComment>
  async likeComment(request: CommentLikeRequest): Promise<CommentLikeResponse>
  async likeComment(
    commentIdOrRequest: number | CommentLikeRequest,
  ): Promise<CommunityDocumentComment | CommentLikeResponse> {
    let commentId: number

    if (typeof commentIdOrRequest === 'number') {
      commentId = commentIdOrRequest
    } else {
      commentId = commentIdOrRequest.commentId
    }

    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: `/comment/${commentId}/like?entityType=DOCU_COMMENT`,
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<ServerResponse<CommunityDocumentComment>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 댓글 좋아요 실패')
    }

    if (typeof commentIdOrRequest === 'object') {
      // Return CommentLikeResponse for new API
      return {
        commentId,
        thumbUpCount: response.data.data.thumbUpCount || 0,
        hasThumbUp: true,
      }
    }

    return response.data.data
  }

  async cancelLikeComment(commentId: number): Promise<CommunityDocumentComment>
  async cancelLikeComment(request: CommentLikeRequest): Promise<CommentLikeResponse>
  async cancelLikeComment(
    commentIdOrRequest: number | CommentLikeRequest,
  ): Promise<CommunityDocumentComment | CommentLikeResponse> {
    let commentId: number

    if (typeof commentIdOrRequest === 'number') {
      commentId = commentIdOrRequest
    } else {
      commentId = commentIdOrRequest.commentId
    }

    const config = buildApiEndpoint(
      {
        method: 'DELETE',
        url: `/comment/${commentId}/like?entityType=DOCU_COMMENT`,
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<ServerResponse<CommunityDocumentComment>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 댓글 좋아요 취소 실패')
    }

    if (typeof commentIdOrRequest === 'object') {
      // Return CommentLikeResponse for new API
      return {
        commentId,
        thumbUpCount: response.data.data.thumbUpCount || 0,
        hasThumbUp: false,
      }
    }

    return response.data.data
  }

  async deleteComment(documentId: number, commentId: number): Promise<CommunityDocumentComment> {
    const config = buildApiEndpoint(
      {
        method: 'DELETE',
        url: `/document/${documentId}/comment/${commentId}`,
      },
      'solar',
    )

    const response = await this.apiClient.request<ServerResponse<CommunityDocumentComment>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 댓글 삭제 실패')
    }
    return response.data.data
  }

  async getDocumentsByDoctor({
    memberId,
    pageIndex,
    pageSize,
  }: {
    memberId: number
    pageIndex: number
    pageSize: number
  }): Promise<SpringPaginationStandardFetcherResponse<DocumentByDoctor[]>> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/community/queries/get-doctor-community/process',
        data: {
          memberId,
          pagination: {
            pageIndex,
            pageSize,
            pageSorts: [
              {
                pageSortField: 'CREATED_DATE',
                pageSortOrder: 'DESC',
              },
            ],
          },
        },
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )
    const response = await this.apiClient.request<SpringPaginationStandardFetcherResponse<DocumentByDoctor[]>>(config)
    return response.data
  }

  async getCommentsByDoctor({
    memberId,
    pageIndex,
    pageSize,
  }: {
    memberId: number
    pageIndex: number
    pageSize: number
  }): Promise<SpringPaginationStandardFetcherResponse<GetCommentsByDoctorContent[]>> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/community/queries/get-doctor-comment/process',
        data: {
          memberId,
          pagination: {
            pageIndex,
            pageSize,
            pageSorts: [
              {
                pageSortField: 'CREATED_DATE',
                pageSortOrder: 'DESC',
              },
            ],
          },
        },
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )
    const response =
      await this.apiClient.request<SpringPaginationStandardFetcherResponse<GetCommentsByDoctorContent[]>>(config)
    return response.data
  }

  // Medical Tips functionality
  async getMedicalTip(tipId: number, headers?: Record<string, string>): Promise<GetDocumentDetailResponse> {
    try {
      const config = {
        method: 'GET',
        url: `${getEnv('NEXT_PUBLIC_HOST')}/api/v2/community/medical-tips/${tipId}`,
        headers: {
          deviceType: 'BROWSER',
          'accept-language': headers?.['Accept-Language'] ?? 'ko-KR',
          authorization: headers?.Authorization ?? '',
          'x-forwarded-for': headers?.['x-forwarded-for'] ?? '',
        },
      }
      const response = await axios.request<ServerResponse<GetDocumentDetailResponse>>(config)
      if (response.data.succeeded === false) {
        throw new Error(response.data.reason ?? '[Community] 의료 팁 조회 실패')
      }
      return response.data.data
    } catch (error) {
      console.error('getMedicalTip error')
      throw new Error('[Community] 의료 팁 조회 실패')
    }
  }

  async getMedicalTipsSolar(pageIndex = 0, headers?: Record<string, string>): Promise<CommunityMedicalTipView[]> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/community/medical-tip/queries/get-medical-tips/process',
        data: {
          pagination: {
            pageIndex,
            pageSize: 20,
            pageSorts: [
              {
                pageSortField: 'PUBLISHED_DATE',
                pageSortOrder: 'DESC',
              },
            ],
          },
        },
        headers: {
          deviceType: 'BROWSER',
          ...headers,
        },
      },
      'solar',
    )
    const response = await this.apiClient.request<SpringPaginationResult<CommunityMedicalTipView>>(config)
    return response.data.content || []
  }

  async getMedicalTips(pageIndex = 0, headers?: Record<string, string>): Promise<GetDocumentListResponse[]> {
    try {
      const config = {
        method: 'GET',
        url: `${getEnv('NEXT_PUBLIC_HOST')}/api/v2/community/medical-tips`,
        headers: {
          deviceType: 'BROWSER',
          'accept-language': headers?.['Accept-Language'] ?? 'ko-KR',
          authorization: headers?.Authorization ?? '',
          'x-forwarded-for': headers?.['x-forwarded-for'] ?? '',
        },
        params: {
          page: pageIndex,
          length: 20,
        },
      }
      const response = await axios.request<ServerResponse<GetDocumentListResponse[]>>(config)
      if (response.data.succeeded === false) {
        throw new Error(response.data.reason ?? '[Community] 의료 팁 조회 실패')
      }
      return response.data.data
    } catch (error) {
      console.error('getMedicalTips error')
      throw new Error('[Community] 의료 팁 조회 실패')
    }
  }

  // Community Recommendations functionality
  async getCommunityRecommendations({
    communityId,
    totalContentsCount,
  }: {
    communityId: number
    totalContentsCount: number
  }): Promise<CommunityRecommendation[]> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: `/community/queries/get-community-recommendations/process`,
        data: {
          communityId,
          totalContentsCount,
          pagination: {
            pageIndex: 0,
            pageSize: totalContentsCount,
            pageSorts: [],
          },
        },
        headers: {
          deviceType: 'BROWSER',
        },
      },
      'solar',
    )
    const response =
      await this.apiClient.request<SpringPaginationStandardFetcherResponse<CommunityRecommendation[]>>(config)
    return response.data.content
  }

  // ========================
  // API-CLIENT INTEGRATION - New methods using api-client
  // ========================

  // Document operations via API-client
  /**
   * @method UserApiDocument.getDocuments
   * @path GET /community/document
   * @returns Promise<CommunityDocument[]>
   */
  getDocumentsFromApi = (queries?: { start?: number; length?: number }) =>
    this.documentApiReader.getDocuments(queries || {})

  /**
   * @method UserApiDocument.getDocumentCategories
   * @path GET /community/document/categories
   * @returns Promise<DocumentCategory[]>
   */
  getDocumentCategoriesFromApi = (payload: { memberType: 'MEMBER' | 'ADMIN'; permission: 'READ' | 'WRITE' }) =>
    this.documentApiReader.getDocumentCategories(payload)

  /**
   * @method UserApiDocument.postDocument
   * @path POST /community/document
   * @returns Promise<PostCommunityDocumentResponse>
   */
  postDocumentFromApi = (payload: PostCommunityDocumentParam) => this.documentApiReader.postDocument(payload)

  // Medical Tips operations via API-client
  /**
   * @method CommunityMedicalTip.getMedicalTips
   * @path GET /community/medical-tips
   * @returns Promise<CommunityMedicalTipResponse[]>
   */
  getMedicalTipsFromApi = (query: CommunityMedicalTipPagination) => this.medicalTipApiReader.getMedicalTips(query)

  /**
   * @method CommunityMedicalTip.getMedicalTip
   * @path GET /community/medical-tips/{tipId}
   * @returns Promise<CommunityMedicalTipDetail>
   */
  getMedicalTipFromApi = (query: { tipId: number }) => this.medicalTipApiReader.getMedicalTip(query)

  // Doctor community operations via API-client
  /**
   * @method UserApiCommunityOfDoctorReader.getDocuments
   * @path GET /community/doctor/{memberId}/documents
   * @returns Promise<DoctorCommunityDocument[]>
   */
  getDocumentsOfDoctorFromApi = (payload: {
    memberId: number
    pagination: {
      pageIndex: number
      pageSize: number
      pageSorts: {
        pageSortField: PageSortField
        pageSortOrder: PageSortOrder
      }[]
    }
  }) => this.doctorApiReader.getDocuments(payload)

  /**
   * @method UserApiCommunityOfDoctorReader.getComments
   * @path GET /community/doctor/{memberId}/comments
   * @returns Promise<DoctorCommunityComment[]>
   */
  getCommentsOfDoctorFromApi = (payload: {
    memberId: number
    pagination: {
      pageIndex: number
      pageSize: number
      pageSorts: {
        pageSortField: PageSortField
        pageSortOrder: PageSortOrder
      }[]
    }
  }) => this.doctorApiReader.getComments(payload)

  // ========================
  // iOS API SPEC COMPLIANCE - Enhanced methods (using buildApiEndpoint pattern)
  // ========================

  // Alias for enhanced API compatibility
  getDocumentComments = this.getComments

  // HOT & WAITING DOCUMENTS (iOS API Spec compliance)
  /**
   * @path GET /document/hot
   * @description Get hot/trending documents
   */
  async getHotDocuments(request: HotDocumentsRequest): Promise<GetDocumentListResponse[]> {
    const config = buildApiEndpoint(
      {
        method: 'GET',
        url: '/document/hot',
        params: {
          start: request.start || 0,
          length: request.length,
          categoryIds: request.categoryIds,
          period: request.period || 'week',
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<ServerResponse<GetDocumentListResponse[]>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 인기 게시글 조회 실패')
    }

    return response.data.data
  }

  /**
   * @path GET /document/wait
   * @description Get documents waiting for doctor response
   */
  async getWaitingDocuments(request: WaitingDocumentsRequest): Promise<GetDocumentListResponse[]> {
    const config = buildApiEndpoint(
      {
        method: 'GET',
        url: '/document/wait',
        params: {
          start: request.start || 0,
          length: request.length,
          categoryIds: request.categoryIds,
          doctorSpecialty: request.doctorSpecialty,
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<ServerResponse<GetDocumentListResponse[]>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 답변 대기 게시글 조회 실패')
    }

    return response.data.data
  }

  /**
   * @path GET /document/subscribe
   * @description Get subscribed documents for a member
   */
  async getSubscribedDocuments(request: SubscribedDocumentsRequest): Promise<GetDocumentListResponse[]> {
    const config = buildApiEndpoint(
      {
        method: 'GET',
        url: '/document/subscribe',
        params: {
          memberId: request.memberId,
          start: request.start || 0,
          length: request.length,
          sort: request.sort || 'updateTime',
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<ServerResponse<GetDocumentListResponse[]>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 구독 게시글 조회 실패')
    }

    return response.data.data
  }

  // DOCTOR SPECIFIC FEATURES - keep existing endpoints
  /**
   * @path POST /community/queries/get-doctor-community/process
   * @description Get documents by specific doctor
   */
  async getDoctorDocuments(request: DoctorCommunityRequest): Promise<DocumentByDoctor[]> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/community/queries/get-doctor-community/process',
        data: {
          doctorId: request.doctorId,
          pagination: {
            pageIndex: Math.floor((request.start || 0) / (request.length || 20)),
            pageSize: request.length || 20,
            pageSorts: [
              {
                pageSortField: 'CREATED_DATE',
                pageSortOrder: 'DESC',
              },
            ],
          },
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<SpringPaginationStandardFetcherResponse<DocumentByDoctor[]>>(config)
    return response.data.content
  }

  /**
   * @path POST /community/queries/get-doctor-comment/process
   * @description Get comments by specific doctor
   */
  async getDoctorComments(request: DoctorCommentRequest): Promise<GetCommentsByDoctorContent[]> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/community/queries/get-doctor-comment/process',
        data: {
          doctorId: request.doctorId,
          pagination: {
            pageIndex: Math.floor((request.start || 0) / (request.length || 20)),
            pageSize: request.length || 20,
            pageSorts: [
              {
                pageSortField: 'CREATED_DATE',
                pageSortOrder: 'DESC',
              },
            ],
          },
        },
      },
      'solar',
    )

    const response =
      await this.apiClient.request<SpringPaginationStandardFetcherResponse<GetCommentsByDoctorContent[]>>(config)
    return response.data.content
  }

  // PERSONALIZATION FEATURES
  /**
   * @path POST /community/queries/get-community-personalized-ids/process
   * @description Get personalized community content IDs
   */
  async getPersonalizedIds(request: PersonalizedIdsRequest): Promise<PersonalizedIdsResponse> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/community/queries/get-community-personalized-ids/process',
        data: {
          memberId: request.memberId,
          count: request.count || 20,
          excludeIds: request.excludeIds || [],
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<ServerResponse<PersonalizedIdsResponse>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 개인화 콘텐츠 ID 조회 실패')
    }

    return response.data.data
  }

  // TRANSLATION FEATURES
  /**
   * @path POST /translate
   * @description Translate text content
   */
  async translateContent(request: TranslationRequest): Promise<TranslationResponse> {
    const config = buildApiEndpoint(
      {
        method: 'POST',
        url: '/translate',
        data: {
          text: request.text,
          targetLanguage: request.targetLanguage,
          sourceLanguage: request.sourceLanguage,
        },
      },
      'solar',
    )

    const response = await this.apiClient.request<ServerResponse<TranslationResponse>>(config)
    if (response.data.succeeded === false) {
      throw new Error(response.data.reason ?? '[Community] 번역 처리 실패')
    }

    return response.data.data
  }
}

export const clientCommunityDocumentService = new CommunityDocumentService(createSolarApiClient())
